import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2, Plus, User as UserIcon, LogOut, Users, Home as HomeIcon,
  Inbox, Mail, Lightbulb, Radar, Activity, ListChecks, MapPin, Brain,
  Briefcase, Layers, Settings, Menu as MenuIcon, X, FileText, Calendar,
} from 'lucide-react';
import DropdownMenu from './DropdownMenu';

/**
 * Header consolidado do Dashboard.
 * Substitui a barra de 18 botões por: Logo + "+ Novo" + nav principal + comms + intel + ⚙ + user.
 *
 * Props: todos os handlers existentes do Dashboard (open*, user, emailStats, f8PendingCount, onLogout).
 */
export default function DashboardHeader({
  user,
  emailStats,
  f8PendingCount,
  onLogout,
  onOpenCreateEmpresa,
  onOpenCreateOferta,
  onOpenCreateArea,
  onOpenAreasList,
  onOpenInbox,
  onOpenEmailsHub,
  onOpenOportunidades,
  onOpenDominios,
  onOpenApollo,
  onOpenF8,
  onOpenAIContext,
}) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const novoItems = [
    { label: 'Empresa', icon: Building2, onClick: onOpenCreateEmpresa },
    { label: 'Contato', icon: UserIcon, to: '/contatos', hint: 'na lista' },
    { label: 'Área', icon: MapPin, onClick: onOpenCreateArea },
    { label: 'Oferta', icon: Briefcase, onClick: onOpenCreateOferta },
    { label: 'Imóvel', icon: HomeIcon, to: '/imoveis', hint: 'na lista' },
    { label: 'Lote', icon: Layers, disabled: true, hint: 'em breve' },
  ];

  const settingsItems = [
    { label: 'Áreas', icon: MapPin, onClick: onOpenAreasList },
    { label: 'Ofertas', icon: Briefcase, to: '/ofertas' },
    { label: 'Proprietários', icon: Users, to: '/proprietarios' },
    { label: 'Oportunidades', icon: Lightbulb, onClick: onOpenOportunidades },
    { label: 'Domínios', icon: Radar, onClick: onOpenDominios },
    { divider: true },
    { label: 'Intake', icon: FileText, to: '/intake' },
    { label: 'Sync Status', icon: Activity, to: '/sync' },
    { label: 'Sync Health', icon: Activity, to: '/ai-health/gmail-sync' },
    { label: 'Coverage', icon: Mail, to: '/ai-health/email-coverage' },
    { label: 'Fila Empresas', icon: ListChecks, to: '/fila-empresas' },
    { divider: true },
    { label: 'F8 — Revisão IA', icon: Brain, onClick: onOpenF8, badge: f8PendingCount },
    { label: 'Contexto IA', icon: Brain, onClick: onOpenAIContext },
  ];

  const mainLinks = [
    { to: '/', icon: HomeIcon, label: 'Dashboard' },
    { to: '/contatos', icon: Users, label: 'Contatos' },
    { to: '/imoveis', icon: HomeIcon, label: 'Imóveis' },
    { to: '/agenda', icon: Calendar, label: 'Agenda' },
    { to: '/knowledge', icon: Brain, label: 'Knowledge' },
  ];

  const NavLink = ({ to, icon: Icon, label }) => (
    <Link
      to={to}
      className={`px-2.5 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
        isActive(to)
          ? 'text-white font-bold'
          : 'text-white/80 hover:text-white hover:bg-white/10'
      }`}
    >
      <Icon size={14} /> {label}
    </Link>
  );

  const Separator = () => (
    <div className="hidden md:block h-5 w-px bg-white/30 mx-1" />
  );

  return (
    <div className="bg-slate-800 shadow-sm border-b border-slate-900">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2.5 flex items-center gap-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1.5 text-white hover:text-white flex-shrink-0 pr-2">
          <Building2 size={20} className="text-blue-400" />
          <span className="font-bold text-base hidden sm:inline">REIP</span>
        </Link>

        {/* + Novo (primário, sempre visível) */}
        <DropdownMenu
          trigger={<><Plus size={14} /> <span className="hidden sm:inline">Novo</span></>}
          triggerClassName="px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium"
          triggerTitle="Criar novo registro"
          items={novoItems}
          menuWidth="w-48"
          align="left"
        />

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-0.5 flex-1 min-w-0">
          {mainLinks.map((l) => <NavLink key={l.to} {...l} />)}

          <Separator />

          {/* Comunicação */}
          <button
            onClick={onOpenInbox}
            className="relative px-2.5 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:text-white hover:bg-white/10"
            title="Inbox — emails aguardando classificação"
          >
            <Inbox size={14} /> Inbox
            {emailStats?.total > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                {emailStats.total > 99 ? '99+' : emailStats.total}
              </span>
            )}
          </button>
          <button
            onClick={onOpenEmailsHub}
            className="px-2.5 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:text-white hover:bg-white/10"
            title="Buscar em todos os emails"
          >
            <Mail size={14} /> Emails
          </button>

          <Separator />

          {/* Inteligência */}
          <button
            onClick={onOpenApollo}
            className="px-2.5 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:text-white hover:bg-white/10"
            title="Enriquecer empresas via Apollo"
          >
            <span className="text-emerald-400">●</span> Apollo
          </button>
          <NavLink to="/audiencias" icon={Radar} label="Audiências" />

          <Separator />

          {/* Engrenagem */}
          <DropdownMenu
            trigger={<Settings size={15} />}
            triggerClassName="px-2 py-1.5 rounded text-white/80 hover:text-white hover:bg-white/10"
            triggerTitle="Mais ferramentas e operações"
            items={settingsItems}
            menuWidth="w-56"
            badge={f8PendingCount}
          />
        </div>

        {/* Mobile hambúrguer */}
        <div className="lg:hidden flex-1" />
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden p-1.5 rounded text-white/80 hover:text-white hover:bg-white/10"
          title="Menu"
        >
          {mobileOpen ? <X size={18} /> : <MenuIcon size={18} />}
        </button>

        {/* User + logout — direita */}
        <div className="hidden md:flex items-center gap-1.5 px-2 py-1 bg-white/10 rounded text-xs text-white/90">
          <UserIcon size={12} /> {user?.email?.split('@')[0]}
        </div>
        <button
          onClick={onLogout}
          className="p-1.5 rounded text-white/80 hover:text-white hover:bg-red-500/80"
          title="Sair"
        >
          <LogOut size={14} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-700 bg-slate-800 px-4 py-2 space-y-0.5">
          {mainLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className={`block px-2 py-1.5 rounded text-sm flex items-center gap-1.5 ${
                isActive(l.to) ? 'bg-white/10 text-white font-semibold' : 'text-white/80 hover:bg-white/10'
              }`}
            >
              <l.icon size={14} /> {l.label}
            </Link>
          ))}
          <div className="border-t border-white/10 my-1" />
          <button
            onClick={() => { setMobileOpen(false); onOpenInbox(); }}
            className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
          >
            <Inbox size={14} /> Inbox {emailStats?.total > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full px-1.5">{emailStats.total}</span>}
          </button>
          <button
            onClick={() => { setMobileOpen(false); onOpenEmailsHub(); }}
            className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
          >
            <Mail size={14} /> Emails
          </button>
          <button
            onClick={() => { setMobileOpen(false); onOpenApollo(); }}
            className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
          >
            <span className="text-emerald-400">●</span> Apollo
          </button>
          <Link
            to="/audiencias"
            onClick={() => setMobileOpen(false)}
            className="block w-full px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
          >
            <Radar size={14} /> Audiências
          </Link>
          <div className="border-t border-white/10 my-1" />
          {settingsItems.filter((i) => !i.divider).map((item) => {
            if (item.to) {
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="block w-full px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
                >
                  <item.icon size={14} /> {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.label}
                onClick={() => { setMobileOpen(false); item.onClick?.(); }}
                className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 text-white/80 hover:bg-white/10"
              >
                <item.icon size={14} /> {item.label}
                {item.badge > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full px-1.5">{item.badge}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
